import {ActivatedRoute, Router} from '@angular/router';
import {ValidatorFn, Validators} from '@angular/forms';

import {switchMap} from 'rxjs/operators';
import {Subject, Subscription} from 'rxjs';

import * as moment from 'moment';

import {
    ApiError,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    ReportingNetService,
    TradingGroupWithAccountsModel,
    ReportType,
    IAccountAllowedCurrencies
} from '@spa/api';

import {ReportsService} from '@spa/reporting';

import {AlertTypesEnum, ColumnSizesEnum, UnspecifiedKeysEnum} from '../../../shared/enums';
import {AlertsService, CacheCollectionService} from '../../../shared/services';
import {BaseControl, FilterControl, Helper, SelectControl} from '../../../shared/classes';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';

import {EventsService} from '../services';
import {LayoutConfigInterface} from '../components';
import {PanelReportsAccountsTrait} from '../traits';
import {PanelReportsResolverDataInterface} from '../interfaces';
import {ReportTypesEnum, PanelReportsAccountsResolverErrorStatesEnum} from '../enums';

import {BaseComponent} from './base-component';

import {
    ErrorMessageIdSelector,
    PanelReportsFilterDateSelector,
    PanelReportsFilterDateToSelector,
    PanelReportsFilterDateFromSelector,
    PanelReportsFilterReportTypeSelector,
    PanelReportsFilterGroupControlSelector,
    PanelReportsFilterCurrencySelector
} from '../../../../assets/descriptors';

import {PanelReportsAccountsMessages} from '../../../../assets/messages';
import {CommonMessages} from 'appAssets/messages/common.messages';
import {environment} from 'appEnv/environment';
import { SelectOptionInterface } from 'appShared/components';

export abstract class TradingReportComponent extends BaseComponent {
    private filterData: { [key: string]: string | number | boolean };
    protected lastFilterData: { [key: string]: string | number | boolean } = {};

    protected activatedRouteSubscription: Subscription;
    protected areGroupReports: boolean;
    protected groups: TradingGroupWithAccountsModel[];

    protected filterDataSubject: Subject<{ [key: string]: string | number | boolean }>;
    protected groupReferenceId: string;

    public reportType: number;
    public timeFormat: string;
    public dateTimeFormat: string;
    public hasReportData: boolean;
    public hiddenPreloader: boolean;
    public reportDateDifference: number;
    public filterControls: FilterControl[];
    public reportData: ReportType | ReportType[];
    public reportTypesEnum: typeof ReportTypesEnum;
    public controlsValidationChanged: Subject<{ name: string, validators: ValidatorFn[] }>;
    public updateValueAndValidity: Subject<{name: string, validators: ValidatorFn[], value: any, opts?: {onlySelf?: boolean; emitEvent?: boolean}}>;
    public exportTriggers: { [reportType: number]: Subject<string> };

    protected constructor(
        protected router: Router,
        protected activatedRouter: ActivatedRoute,
        protected reportingNetService: ReportingNetService,
        protected cacheCollectionService: CacheCollectionService,
        protected alertsService: AlertsService,
        public eventsService: EventsService,
        protected reportsService: ReportsService,
    ) {
        super(eventsService, router, cacheCollectionService);

        this.reportType = null;
        this.hiddenPreloader = true;
        this.areGroupReports = false;
        this.reportTypesEnum = ReportTypesEnum;
        this.controlsValidationChanged = new Subject();
        this.updateValueAndValidity = new Subject();
        this.exportTriggers = Object.keys(ReportTypesEnum).reduce((
            triggers: { [reportType: number]: Subject<string> },
            reportType: string
        ) => {
            triggers[parseInt(reportType, 10)] = new Subject();

            return triggers;
        }, {});

        this.timeFormat = environment.timeFormat;
        this.dateTimeFormat = environment.dateTimeFormat;
    }

    protected validateFilterControls(): boolean {
        const dateControl = this.filterControls.find((filterControl: FilterControl) => {
            return (filterControl.name === PanelReportsFilterDateSelector);
        });

        if (dateControl) {
            const dateFromControl = dateControl.controls.find((control: BaseControl) => {
                return (control.name === PanelReportsFilterDateFromSelector);
            });

            if (dateFromControl && dateFromControl.value) {
                const errors = dateFromControl.errors;
                errors.splice(0, errors.length);

                const dateToControl = dateControl.controls.find((control: BaseControl) => {
                    return (control.name === PanelReportsFilterDateToSelector);
                });

                if (dateToControl && dateToControl.value) {
                    const dateFromTimeStamp = moment(dateFromControl.value).unix();
                    const dateToTimeStamp = moment(dateToControl.value).unix();

                    if (dateFromTimeStamp > dateToTimeStamp) {

                        dateFromControl.setError(PanelReportsAccountsMessages.WrongDateFromValue.message);

                        return false;
                    }

                    return true;
                }

                return false;
            }

            return false;
        }

        return true;
    }

    private getReportRequest(data: { [key: string]: string | number | boolean }): ServerResponse<ReportType> {
        const dateFromValue = data[PanelReportsFilterDateFromSelector] as string;
        const dateToValue = data[PanelReportsFilterDateToSelector] as string;
        const currency = data[PanelReportsFilterCurrencySelector] as string;

        this.filterData = data;
        this.reportDateDifference = moment(dateToValue).diff(moment(dateFromValue), 'days');

        if (this.areGroupReports) {
            return PanelReportsAccountsTrait.getGroupReports(
                this.reportType,
                this.groupReferenceId,
                dateFromValue,
                dateToValue,
                this.reportingNetService,
                currency === UnspecifiedKeysEnum.UnspecifiedCurrencyKey  ? null : currency
            );
        }

        return PanelReportsAccountsTrait.getAccountReport(
            this.reportType,
            this.getAccountsRefIds(data) as string,
            dateFromValue,
            dateToValue,
            this.reportingNetService,
            this.reportsService,
            currency
        );
    }

    private getReportSubscription(response: ServerResponseData<ReportType>): void {
        this.hiddenPreloader = true;

        if (response.status !== HttpStatusCodesEnum.Ok) {
            if (response.status === HttpStatusCodesEnum.NotAcceptable) {
                this.alertsService.addAlert(
                    'The request has timed out. Please try again.',
                    AlertTypesEnum.Error);
                this.hasReportData = false;
            }
            else if (response.status === HttpStatusCodesEnum.Unauthorized) {
                this.router.navigate(['/authentication', 'login']).finally();
            } else if (response.status === HttpStatusCodesEnum.Forbidden) {
                const timestamp = moment().unix();

                if (this.cacheCollectionService) {
                    this.cacheCollectionService.addItem(timestamp.toString(), [CommonMessages.ForbiddenError.message]);
                }

                this.router.navigate(['/error'], {
                    queryParams: {
                        [ErrorMessageIdSelector]: timestamp
                    }
                }).finally();
            }
            else if (response.status === HttpStatusCodesEnum.Forbidden) {
                this.redirectToErrorPage(CommonMessages.ForbiddenError.message);
            }
            else if (response.status !== HttpStatusCodesEnum.NotFound) {
                const reportName = !this.areGroupReports ?
                    Helper.textsHelper().getAccountReportName(this.reportType) :
                    Helper.textsHelper().getGroupReportName(this.reportType);

                const message = PanelReportsAccountsMessages.GetReportFailed.message
                    .replace('<< ReportName >>', `${reportName} report`);

                this.redirectToErrorPage(message);
            }
            else {
                this.hasReportData = false;
            }
        } else {
            if (!response.data){
                this.hasReportData = false;
                this.reportData = null;

                return;
            }
            this.hasReportData = true;
            if ((response.data as any[]).length === 0){
                this.reportData = null;
            }

            this.reportData = this.reportType === ReportTypesEnum.TotalsByAccount ? response.data as any[]
                : (response.data as any[])[0] as ReportType;
        }
    }

    private redirectToErrorPage(messages: string) {
        const timestamp = moment().unix();

        if (this.cacheCollectionService) {
            this.cacheCollectionService.addItem(timestamp.toString(), [messages]);
        }

        this.router.navigate(['/error'], {
            queryParams: {
                [ErrorMessageIdSelector]: timestamp
            }
        }).finally();
    }

    protected UpdateCurrencyControl(options: SelectOptionInterface[], val: number | string){
        const currencyFormControl = this.filterControls.find(
            (control) => control.name === PanelReportsFilterCurrencySelector
        );
        if (currencyFormControl){
            const currencyControl = currencyFormControl
            .controls[0] as SelectControl;
            if (currencyControl){
                currencyControl.options.splice(0, currencyControl.options.length);
                currencyControl.options.push(...options);
                currencyControl.value = val;
                currencyControl.disabled = currencyControl.options.length === 1;
                currencyControl.resetErrors();
                currencyControl.resetValidators();
                currencyControl.extendValidators(Validators.required);
                this.updateValueAndValidity.next({name: currencyControl.name,
                    validators: currencyControl.validators,
                    value: currencyControl.value,
                    opts: {
                    emitEvent: false
                }});
            }
        }
    }

    protected updateGroupControlValidity(reportType: number): void {
        const groupControl = this.filterControls.find((filterControl: FilterControl) => {
            return (filterControl.name === PanelReportsFilterGroupControlSelector);
        });

        if (groupControl) {
            const groupInnerControl = groupControl.controls.find((control: BaseControl) => {
                return (control.name === PanelReportsFilterGroupControlSelector);
            });

            if (groupInnerControl) {
                groupInnerControl.resetErrors();
                groupInnerControl.resetValidators();
                groupInnerControl.disabled = reportType === ReportTypesEnum.TotalsByGroup;

                if (reportType !== ReportTypesEnum.TotalsByGroup) {
                    groupInnerControl.extendValidators(Validators.required);
                }

                this.controlsValidationChanged.next({name: groupInnerControl.name, validators: groupInnerControl.validators});
            }
        }
    }

    abstract getAccountsRefIds(data?: { [key: string]: string | number | boolean }): string | string[];

    abstract fixReportName(data: { [key: string]: string | number | boolean }): string;

    onInit(): void {
        super.onInit();

        this.filterDataSubject = new Subject();

        this.activatedRouteSubscription = this.activatedRouter.data
            .subscribe((data: { resolverData: ServerResponseData<PanelReportsResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(data.resolverData, () => {
                    this.filterDataSubject.pipe(
                        switchMap((filterData: { [key: string]: string | number | boolean }) => this.getReportRequest(filterData))
                    ).subscribe(reportsResponse => this.getReportSubscription(reportsResponse));

                    this.filterControls = (data.resolverData.data as PanelReportsResolverDataInterface).filterControls;
                    this.groups = (data.resolverData.data as PanelReportsResolverDataInterface).groups;
                    this.filterControls.forEach(x => {
                        x.controls.forEach(y => {
                            this.lastFilterData[y.name] = y.value;
                        });
                    });
                });
            });
    }

    getLayoutConfig(): LayoutConfigInterface {
        return {
            mainColumnSize: ColumnSizesEnum.Col12,
            subColumnSize: null
        };
    }

    getText(): string {
        return '';
    }

    onFilterChanged(data: { [key: string]: string | number | boolean }): void {
        if (this.validateFilterControls()) {
            this.cacheFilterValues(data);
            this.reportType = data[PanelReportsFilterReportTypeSelector] as number;
            this.reportData = null;
            this.hiddenPreloader = false;
            this.filterDataSubject.next(data);
        }
    }
    SaveLastFilter(data: { [key: string]: string | number | boolean }): void {
         this.lastFilterData = data;
    }

    triggerReportExport(reportType: number) {
        if (typeof this.exportTriggers[reportType] !== 'undefined') {
            const reportName = (!this.areGroupReports ? Helper.textsHelper().getAccountReportName(this.reportType) :
                Helper.textsHelper().getGroupReportName(this.reportType)).replace(/\s+/g, '').toUpperCase();

            this.exportTriggers[reportType].next(reportName + this.fixReportName(this.filterData));
        }
    }

    validateInitialData(response: ServerResponseData<PanelReportsResolverDataInterface>): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            const messages = (response.data as ApiError[]).map((error: ApiError) => {
                if (error.status === PanelReportsAccountsResolverErrorStatesEnum.GetTradingGroupsFailed) {
                    return PanelReportsAccountsMessages.GetTradingGroupsFailed.message;
                }
                if (error.status === PanelReportsAccountsResolverErrorStatesEnum.GetCurrencyListFailed) {
                    return PanelReportsAccountsMessages.GetCurrencyListFailed.message;
                }

                return PanelReportsAccountsMessages.UnknownError.message;
            });

            throw new InitialComponentDataValidationException(messages);
        }
    }
}
